import type Phaser from "phaser";

/**
 * The leaves a blow throws from the prop being harvested (ADR-142's amendment of 2026-10-31): a few green pixels that
 * fly up and out and fall, fading, over a little under half a second (`LEAF_MS`). A picture of the page alone; nothing is kept.
 */

/**
 * How long a burst lasts, in milliseconds.
 */
export const LEAF_MS = 450;

/**
 * Each leaf's sideways reach and its rise, in world pixels, and its colour: leaves go left and right of the prop's middle.
 */
export const LEAVES: readonly { readonly across: number; readonly rise: number; readonly colour: number }[] = [
  { across: -11, rise: 15, colour: 0x7cc84a },
  { across: -5, rise: 21, colour: 0x4f9a3a },
  { across: 2, rise: 18, colour: 0x9ad65a },
  { across: 7, rise: 22, colour: 0x4f9a3a },
  { across: 12, rise: 14, colour: 0x7cc84a },
];

/**
 * How far a leaf has fallen back by the end, in world pixels below where it started.
 */
const FALL = 6;

/**
 * Where a leaf is, at a share of the burst's time.
 *
 * @param leaf The leaf, by its place in `LEAVES`.
 * @param t The share of the burst gone, 0 to 1.
 * @returns How far across and how far down of its start, in world pixels, and its alpha.
 * @example
 * leafAt(0, 0);
 * // { x: 0, y: 0, alpha: 1 }
 */
export function leafAt(leaf: number, t: number): { x: number; y: number; alpha: number } {
  const spec = LEAVES[leaf] ?? (LEAVES[0] as (typeof LEAVES)[number]);
  const rise = spec.rise * Math.sin(Math.PI * Math.min(1, t) * 0.8);
  return { x: t === 0 ? 0 : spec.across * t, y: -rise + FALL * t * t, alpha: 1 - t };
}

/**
 * Draws bursts of leaves into a scene.
 */
export class HarvestLeaves {
  /**
   * @param scene The scene to draw into.
   * @param depthOf The depth a burst draws at for a feet line in world pixels: just in front of the prop's own row.
   */
  public constructor(
    private readonly scene: Phaser.Scene,
    private readonly depthOf: (feetY: number) => number,
  ) {}

  /**
   * Throws a burst of leaves from a point; the pieces end themselves.
   *
   * @param x The point across, in world pixels.
   * @param y The point down.
   * @param feetY The prop's feet line, which the burst sorts by.
   */
  public burst(x: number, y: number, feetY: number): void {
    const tweens = this.scene.tweens as Phaser.Tweens.TweenManager | undefined;
    if (tweens === undefined) {
      return;
    }
    const depth = this.depthOf(feetY + 2);
    const pieces = LEAVES.map((leaf) => this.scene.add.rectangle(x, y, 2, 1, leaf.colour).setDepth(depth));
    tweens.addCounter({
      from: 0,
      to: 1,
      duration: LEAF_MS,
      onUpdate: (counter: Phaser.Tweens.Tween) => {
        const t = counter.getValue() ?? 0;
        pieces.forEach((piece, leaf) => {
          const at = leafAt(leaf, t);
          piece.setPosition(x + Math.round(at.x), y + Math.round(at.y)).setAlpha(at.alpha);
        });
      },
      onComplete: () => {
        for (const piece of pieces) {
          piece.destroy();
        }
      },
    });
  }
}
